import { Box, FormControlLabel, Grid, MenuItem, Radio, RadioGroup, Select, Switch, TextField, Typography } from "@mui/material";
import React from "react";
import { DatePicker } from "source/components/framework/DateTime";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { Base } from "ui/Scripts/source/framework/base";
import { CalendarTaskRepetitionType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";

/* eslint-disable no-unused-vars */
enum CalendarTaskRepetitionEndType {
    Count = "0",
    Date = "1"
}
/* eslint-enable no-unused-vars */

export interface IRepetitionIntervalYear {
    day: number;
    month: number;
}

interface CalendarTaskRepetitionSettingsProps {
    orgStartDate: number;
    startDateStr: string;
    isReadOnly: boolean;
    repetitionIntervalType: CalendarTaskRepetitionType;
    repetitionInterval: number;
    repetitionIntervalWeek: number[];
    repetitionIntervalMonth: number;
    repetitionIntervalYear: IRepetitionIntervalYear;
    repetitionEndRepeatCount: number;
    repetitionEndDateStr: string;
    onChange: (name: string, value: string | number | IRepetitionIntervalYear | number[]) => void;
}

export const CalendarTaskRepetitionSettings = (
    props: CalendarTaskRepetitionSettingsProps
) => {
    const getDefaultRepetitionIntervalCount = (repetitionIntervalType: CalendarTaskRepetitionType) => {
        let result = 0;
        if (repetitionIntervalType === CalendarTaskRepetitionType.Weekly) {
            result = 10;
        } else if (repetitionIntervalType === CalendarTaskRepetitionType.Monthly) {
            result = 12;
        } else {
            result = 2;
        }
        return result;
    };

    const getDefaultRepetitionIntervalWeek = (): number[] => {
        const orgStartDate = props.orgStartDate ? new Date(props.orgStartDate) : new Date();
        return [((props.startDateStr ? props.startDateStr.toDate(orgStartDate) : orgStartDate).getDay() + 6) % 7];
    };

    const getDefaultRepetitionIntervaMonth = (): number => {
        const orgStartDate = props.orgStartDate ? new Date(props.orgStartDate) : new Date();
        return (props.startDateStr ? props.startDateStr.toDate(orgStartDate) : orgStartDate).getDate();
    };

    const getDefaultRepetitionIntervalYear = (): IRepetitionIntervalYear => {
        const orgStartDate = props.orgStartDate ? new Date(props.orgStartDate) : new Date();
        const date = (props.startDateStr ? props.startDateStr.toDate(orgStartDate) : orgStartDate);
        return { month: date.getMonth() + 1, day: date.getDate() };
    };

    //const handleRepetitionChange = (_event: React.MouseEvent<HTMLElement>, type: number) => {
    const handleRepetitionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const type = (event.target as HTMLInputElement).value.toInteger();
        const repetitionEnabledFirstTime = props.repetitionInterval < 1 && type > CalendarTaskRepetitionType.NotRepeated;
        const weekRepetitionEnabled = props.repetitionIntervalType !== CalendarTaskRepetitionType.Weekly && type === CalendarTaskRepetitionType.Weekly;
        const monthRepetitionEnabled = props.repetitionIntervalType !== CalendarTaskRepetitionType.Monthly && type === CalendarTaskRepetitionType.Monthly;
        const yearRepetitionEnabled = props.repetitionIntervalType !== CalendarTaskRepetitionType.Yearly && type === CalendarTaskRepetitionType.Yearly;
        props.onChange("repetitionIntervalType", type);
        if (repetitionEnabledFirstTime) {
            props.onChange("repetitionInterval", 1);
            props.onChange("repetitionEndRepeatCount", getDefaultRepetitionIntervalCount(type));
        }
        if (weekRepetitionEnabled) {
            props.onChange("repetitionIntervalWeek", getDefaultRepetitionIntervalWeek());
        } else if (monthRepetitionEnabled) {
            props.onChange("repetitionIntervalMonth", getDefaultRepetitionIntervaMonth());
        } else if (yearRepetitionEnabled) {
            props.onChange("repetitionIntervalYear", getDefaultRepetitionIntervalYear());
        }
    };

    const handleRepetitionIntervalChange = (event) => {
        const value: string = event.target.value;
        props.onChange("repetitionInterval", Math.min(value.toInteger(false, 1), 99));
    };

    const handleRepetitionEndChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === "0") {
            props.onChange("repetitionEndRepeatCount", getDefaultRepetitionIntervalCount(props.repetitionIntervalType));
            props.onChange("repetitionEndDateStr", "");
        } else {
            let repetitionEndDate = Base.getNowUtcDate();
            if (props.repetitionIntervalType === CalendarTaskRepetitionType.Weekly) {
                repetitionEndDate = repetitionEndDate.addMonths(2);
            } else if (props.repetitionIntervalType === CalendarTaskRepetitionType.Monthly) {
                repetitionEndDate = repetitionEndDate.addMonths(12);
            } else {
                repetitionEndDate = repetitionEndDate.addYears(2);
            }
            props.onChange("repetitionEndRepeatCount", 0);
            props.onChange("repetitionEndDateStr", Base.utcTimeToDateStr(repetitionEndDate.getTime()));
        }
    };
    
    const handleRepetitionEndRepeatCountChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        props.onChange("repetitionEndRepeatCount", Math.min(value.toInteger(false, 1), 99));
    };
    
    const handleRepetitionEndDateStrChange = (value: string) => {
        const minDate = Base.getNowUtcDate();
        const maxDate = minDate.addYears(10);
        let repetitionEndDate = value.toUtcDate(minDate);
        if (repetitionEndDate.getTime() < minDate.getTime()) {
            repetitionEndDate = minDate;
        } else if (repetitionEndDate.getTime() > maxDate.getTime()) {
            repetitionEndDate = maxDate;
        }
        props.onChange("repetitionEndDateStr", Base.utcTimeToDateStr(repetitionEndDate.getTime()));
    };

    const handleRepetitionYearDayChange = (event) => {
        const value: string = event.target.value;
        const parts = Base.getValidDateParts(Math.min(value.toInteger(false, 1), 31), props.repetitionIntervalYear.month, null);
        const result: IRepetitionIntervalYear = {
            day: parts.day,
            month: parts.month
        };
        props.onChange("repetitionIntervalYear", result);
    };

    const handleRepetitionYearMonthChange = (event) => {
        const value: number = event.target.value;
        const parts = Base.getValidDateParts(props.repetitionIntervalYear.day, Math.min(value, 12), null);
        const result: IRepetitionIntervalYear = {
            day: parts.day,
            month: parts.month
        };
        props.onChange("repetitionIntervalYear", result);
    };

    const handleRepetitionMonthDayChange = (event) => {
        const value: string = event.target.value;
        props.onChange("repetitionIntervalMonth", Math.min(value.toInteger(false, 1), 31));
    };

    const handleRepetitionWeekDayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const day = event.target.name.toInteger();
        const result = props.repetitionIntervalWeek.filter(i => i.toString(10) !== day.toString(10));
        if (event.target.checked) {
            result.push(day);
        }
        if (!result.length) return;
        props.onChange("repetitionIntervalWeek", result);
    };

    const repetitionEndType = props.repetitionEndRepeatCount > 0 ? CalendarTaskRepetitionEndType.Count : CalendarTaskRepetitionEndType.Date;

    return (
        <>
            <GridRow>
                <Grid item sm={12}>
                    <FormField title={Translations.Recurrence} margin={0}>
                        <RadioGroup row onChange={handleRepetitionChange} value={props.repetitionIntervalType}>
                            <FormControlLabel value={CalendarTaskRepetitionType.NotRepeated} control={<Radio />} label={Translations.NotRepeated} disabled={props.isReadOnly} />
                            <FormControlLabel value={CalendarTaskRepetitionType.Weekly} control={<Radio />} label={Translations.Weekly} disabled={props.isReadOnly} />
                            <FormControlLabel value={CalendarTaskRepetitionType.Monthly} control={<Radio />} label={Translations.Monthly} disabled={props.isReadOnly} />
                            <FormControlLabel value={CalendarTaskRepetitionType.Yearly} control={<Radio />} label={Translations.Yearly} disabled={props.isReadOnly} />
                        </RadioGroup>
                    </FormField>
                </Grid>
            </GridRow>
            {props.repetitionIntervalType !== CalendarTaskRepetitionType.NotRepeated && (
                <>
                    <GridRow>
                        <Grid item sm={12}>
                            <Box
                                sx={{ display: "flex", alignItems: "center", margin: "2rem 0 1rem 0" }}
                            >
                                <Typography sx={{ width: "100px" }}>{Translations.CalendarTaskRepetitionRepeatIntervalPrefix}</Typography>
                                <TextField
                                    title={Translations.Week}
                                    type="number"
                                    disabled={props.isReadOnly}
                                    inputProps={{ maxLength: 2 }}
                                    InputProps={{ inputProps: { min: 1, max: 99 } }}
                                    value={props.repetitionInterval}
                                    onChange={handleRepetitionIntervalChange}
                                    sx={{ margin: "0 1rem", width: "100px" }}
                                />
                                <Typography>{props.repetitionIntervalType === CalendarTaskRepetitionType.Weekly
                                    ? Translations.CalendarTaskRepetitionRepeatIntervalWeeklySuffix
                                    : (props.repetitionIntervalType === CalendarTaskRepetitionType.Monthly
                                        ? Translations.CalendarTaskRepetitionRepeatIntervalMonthlySuffix
                                        : Translations.CalendarTaskRepetitionRepeatIntervalYearlySuffix)}</Typography>
                            </Box>
                        </Grid>
                    </GridRow>
                    {props.repetitionIntervalType === CalendarTaskRepetitionType.Weekly && (
                        <Box sx={{ margin: "1rem 0" }}>
                            <GridRow>
                                {[0, 1, 2, 3, 4, 5, 6].map((day) =>
                                    <Grid key={day} item sm={3}>
                                        <FormControlLabel
                                            value={day}
                                            control={
                                                <Switch
                                                    name={day.toString(10)}
                                                    checked={props.repetitionIntervalWeek.findIndex(i => i.toString(10) === day.toString(10)) > -1}
                                                    disabled={props.isReadOnly}
                                                    onChange={handleRepetitionWeekDayChange}
                                                />}
                                            label={Base.dayNumberToName(day + 1, appConfig.culture.replace("-US", ""))}
                                        />
                                    </Grid>
                                )}
                            </GridRow>
                        </Box>
                    )}
                    {props.repetitionIntervalType === CalendarTaskRepetitionType.Monthly &&
                        <GridRow>
                            <Grid item sm={12}>
                                <Box
                                    sx={{ display: "flex", alignItems: "center", margin: "0 0 1rem 0" }}
                                >
                                    <Typography sx={{ width: "100px" }}>{Translations.CalendarTaskRepetitionRepeatIntervalMonthPrefix}</Typography>
                                    <TextField
                                        type="number"
                                        disabled={props.isReadOnly}
                                        inputProps={{ maxLength: 2 }}
                                        InputProps={{ inputProps: { min: 1, max: 31 } }}
                                        value={props.repetitionIntervalMonth}
                                        sx={{ margin: "0 1rem", width: "100px" }}
                                        onChange={handleRepetitionMonthDayChange}
                                    />
                                    <Typography>{Translations.CalendarTaskRepetitionRepeatIntervalMonthSuffix}</Typography>
                                </Box>
                            </Grid>
                        </GridRow>
                    }
                    {props.repetitionIntervalType === CalendarTaskRepetitionType.Yearly &&
                        <GridRow>
                            <Grid item sm={12}>
                                <Box
                                    sx={{ display: "flex", alignItems: "center", margin: "0 0 1rem 0" }}
                                >
                                    <Typography sx={{ width: "100px" }}></Typography>
                                    <TextField
                                        title={Translations.Day}
                                        type="number"
                                        disabled={props.isReadOnly}
                                        inputProps={{ maxLength: 2 }}
                                        InputProps={{ inputProps: { min: 1, max: 31 } }}
                                        sx={{ margin: "0 1rem", width: "100px" }}
                                        value={props.repetitionIntervalYear.day}
                                        onChange={handleRepetitionYearDayChange}
                                    />
                                    {!!Translations.CalendarTaskRepetitionRepeatIntervalDayMonthSeparator &&
                                        <Typography sx={{ marginRight: "1rem" }}>{Translations.CalendarTaskRepetitionRepeatIntervalDayMonthSeparator}</Typography>
                                    }
                                    <Select
                                        value={props.repetitionIntervalYear.month}
                                        disabled={props.isReadOnly}
                                        fullWidth={false}
                                        sx={{ width: "170px" }}
                                        onChange={handleRepetitionYearMonthChange}
                                    >
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) =>
                                            <MenuItem key={month} value={month}>{Base.monthNumberToName(month, appConfig.culture) + (appConfig.culture === "fi" ? "ta" : "")}</MenuItem>
                                        )}
                                    </Select>
                                </Box>
                            </Grid>
                        </GridRow>
                    }
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.CalendarTaskRepetitionEnd}>
                                <RadioGroup value={repetitionEndType} onChange={handleRepetitionEndChange}>
                                    <FormControlLabel
                                        value={CalendarTaskRepetitionEndType.Count}
                                        sx={{ marginBottom: ".5rem!important" }}
                                        label={
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                {!!Translations.CalendarTaskRepetitionCountPrefix &&
                                                    <Typography sx={{ margin: "0 1rem", width: "100px" }}>{Translations.CalendarTaskRepetitionCountPrefix}</Typography>
                                                }
                                                <TextField
                                                    type="number"
                                                    disabled={repetitionEndType === CalendarTaskRepetitionEndType.Date || props.isReadOnly}
                                                    inputProps={{ maxLength: 2 }}
                                                    InputProps={{ inputProps: { min: 1, max: 99 } }}
                                                    value={props.repetitionEndRepeatCount}
                                                    sx={{ width: "185px" }}
                                                    onChange={handleRepetitionEndRepeatCountChange}
                                                />
                                                {!!Translations.CalendarTaskRepetitionCountSuffix &&
                                                    <Typography sx={{ margin: "0 1rem", width: "100px" }}>{Translations.CalendarTaskRepetitionCountSuffix}</Typography>
                                                }
                                            </Box>
                                        }
                                        control={<Radio disabled={props.isReadOnly} />}
                                    />
                                    <FormControlLabel
                                        value={CalendarTaskRepetitionEndType.Date}
                                        sx={{ marginBottom: ".5rem!important" }}
                                        label={
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                {!!Translations.CalendarTaskRepetitionEndDatePrefix &&
                                                    <Typography sx={{ margin: "0 1rem", width: "100px" }}>{Translations.CalendarTaskRepetitionEndDatePrefix}</Typography>
                                                }
                                                <div style={{ width: "185px" }}>
                                                    <DatePicker
                                                        value={props.repetitionEndDateStr}
                                                        disabled={repetitionEndType === CalendarTaskRepetitionEndType.Count || props.isReadOnly}
                                                        onChange={handleRepetitionEndDateStrChange}
                                                    />
                                                </div>
                                                {!!Translations.CalendarTaskRepetitionEndDateSuffix &&
                                                    <Typography sx={{ margin: "0 1rem" }}>{Translations.CalendarTaskRepetitionEndDateSuffix}</Typography>
                                                }
                                            </Box>
                                        }
                                        control={<Radio disabled={props.isReadOnly} />}
                                    />
                                </RadioGroup>
                            </FormField>
                        </Grid>
                    </GridRow>
                </>
            )}
        </>
    );
};
